import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import EditableResultsDisplay from './EditableResultsDisplay';
import InfoPopup from './InfoPopup';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCw, FiHelpCircle } = FiIcons;

const MarginCalculator = ({ 
  darkMode, 
  values, 
  setValues, 
  stickyMargin, 
  setStickyMargin, 
  stickyMarginValue, 
  setStickyMarginValue 
}) => {
  const [popupType, setPopupType] = useState(null);

  const handleInputChange = useCallback((field, value) => {
    setValues(prevValues => {
      const newValues = { ...prevValues, [field]: value };
      
      // If updating Quantity, we don't need to recalculate unit pricing
      if (field === 'qty') return newValues;

      if (stickyMargin && field === 'marginPercentage' && value !== '') {
        setStickyMarginValue(value);
      }

      const cost = parseFloat(field === 'costPrice' ? value : newValues.costPrice);
      const marginPct = parseFloat(field === 'marginPercentage' ? value : (stickyMargin && newValues.marginPercentage === '' ? stickyMarginValue : newValues.marginPercentage));
      const revenue = parseFloat(field === 'revenue' ? value : newValues.revenue);
      const profit = parseFloat(field === 'profit' ? value : newValues.profit);

      if (field === 'costPrice') {
        if (!isNaN(cost)) {
          if (stickyMargin && stickyMarginValue !== '') {
            const sMargin = parseFloat(stickyMarginValue);
            if (sMargin < 100) {
              const newRev = cost / (1 - sMargin / 100);
              newValues.revenue = newRev.toFixed(2);
              newValues.profit = (newRev - cost).toFixed(2);
              newValues.marginPercentage = stickyMarginValue;
            }
          } else if (!isNaN(revenue) && revenue > 0) {
            const newProfit = revenue - cost;
            newValues.profit = newProfit.toFixed(2);
            newValues.marginPercentage = ((newProfit / revenue) * 100).toFixed(2);
          }
        } else {
          newValues.profit = '';
          newValues.revenue = '';
          if (stickyMargin) newValues.marginPercentage = stickyMarginValue;
        }
      } 
      else if (field === 'marginPercentage') {
        const currentMargin = parseFloat(value);
        if (!isNaN(cost) && !isNaN(currentMargin) && currentMargin < 100) {
          const newRev = cost / (1 - currentMargin / 100);
          newValues.revenue = newRev.toFixed(2);
          newValues.profit = (newRev - cost).toFixed(2);
        }
      } 
      else if (field === 'revenue') {
        if (!isNaN(revenue)) {
          if (!isNaN(cost)) {
            const newProfit = revenue - cost;
            newValues.profit = newProfit.toFixed(2);
            newValues.marginPercentage = revenue > 0 ? ((newProfit / revenue) * 100).toFixed(2) : '0.00';
          }
        }
      } 
      else if (field === 'profit') {
        if (!isNaN(profit) && !isNaN(cost)) {
          const newRev = cost + profit;
          newValues.revenue = newRev.toFixed(2);
          newValues.marginPercentage = newRev > 0 ? ((profit / newRev) * 100).toFixed(2) : '0.00';
        }
      }
      return newValues;
    });
  }, [stickyMargin, stickyMarginValue, setValues, setStickyMarginValue]);

  const resetValues = () => {
    setValues({
      costPrice: '',
      marginPercentage: '30.00',
      profit: '',
      revenue: '',
      qty: '1'
    });
    setStickyMarginValue('30.00');
    setStickyMargin(true);
  };

  const toggleStickyMargin = () => {
    const newStickyState = !stickyMargin;
    setStickyMargin(newStickyState);
    if (newStickyState) {
      if (values.marginPercentage !== '') {
        setStickyMarginValue(values.marginPercentage);
      }
    } else {
      setStickyMarginValue('');
    }
  };

  const cost = parseFloat(values.costPrice) || 0;
  const profitValue = parseFloat(values.profit) || 0;
  const markupPercentage = cost > 0 ? (profitValue / cost) * 100 : 0;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 transition-colors duration-300">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">Margin Calculator</h2>
            <button onClick={resetValues} className="flex items-center space-x-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800">
              <SafeIcon icon={FiRefreshCw} /> <span>Reset</span>
            </button>
          </div>

          <EditableResultsDisplay 
            values={values} 
            onChange={handleInputChange} 
            markupPercentage={markupPercentage}
            stickyMargin={stickyMargin}
            toggleStickyMargin={toggleStickyMargin}
            stickyMarginValue={stickyMarginValue}
            darkMode={darkMode}
          />

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <button onClick={() => setPopupType('calculation')} className="w-full flex items-center justify-center space-x-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors duration-200 font-medium text-sm">
              <SafeIcon icon={FiHelpCircle} /> <span>How It's Calculated</span>
            </button>
            <button onClick={() => setPopupType('disclaimer')} className="w-full flex items-center justify-center space-x-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 font-medium text-sm">
              <SafeIcon icon={FiHelpCircle} /> <span>Disclaimer</span>
            </button>
          </div>
        </div>
      </motion.div>
      <InfoPopup isOpen={popupType !== null} onClose={() => setPopupType(null)} type={popupType} darkMode={darkMode} />
    </>
  );
};

export default MarginCalculator;